/**
 * Compatibility exports for code written against the previous icon API
 * (`<Check iconSize="sm-medium" />`), such as the marketing site. They render
 * the same Lucide glyphs as the `Icon*` exports; new code should use those.
 */
import {
  ArrowRight as LucideArrowRight,
  ArrowUpRight as LucideArrowUpRight,
  Check as LucideCheck,
  ChevronDown as LucideChevronDown,
  Copy as LucideCopy,
  FileText as LucideFileText,
  GitBranch as LucideGitBranch,
  GitCommitHorizontal as LucideGitCommitHorizontal,
  type LucideIcon,
  Laptop as LucideLaptop,
  Link as LucideLink,
  Menu as LucideMenu,
  MoonStar as LucideMoonStar,
  Sun as LucideSun,
  X as LucideX,
} from "lucide-react";
import type { CSSProperties } from "react";
import { IconGithub } from "./brands";

type LegacySize = "sm" | "md" | "lg" | "xl" | "2xl";
type LegacyWeight = "thin" | "regular" | "medium" | "bold";
export type iconSize = `${LegacySize}-${LegacyWeight}`;

export type LegacyIconProps = {
  className?: string;
  title?: string;
  iconSize?: iconSize;
  focusable?: boolean;
  style?: CSSProperties;
};

const PIXELS: Record<LegacySize, number> = { sm: 12, md: 14, lg: 16, xl: 18, "2xl": 30 };
// Stroke in screen pixels at 18px; thinner glyphs scale it down with their size.
const STROKE: Record<LegacyWeight, number> = { thin: 1, medium: 1.5, regular: 2, bold: 3 };

function measure(iconSize: iconSize) {
  const [size, weight] = iconSize.split("-") as [LegacySize, LegacyWeight];
  const pixels = PIXELS[size];
  return { pixels, stroke: Math.max(1, (STROKE[weight] * pixels) / 18) };
}

function legacy(Glyph: LucideIcon, displayName: string) {
  function Icon({ iconSize = "xl-thin", title, ...props }: LegacyIconProps) {
    const { pixels, stroke } = measure(iconSize);
    return (
      <Glyph
        size={pixels}
        strokeWidth={stroke}
        absoluteStrokeWidth
        aria-hidden={title ? undefined : true}
        aria-label={title}
        {...props}
      />
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

export const ArrowRight = legacy(LucideArrowRight, "ArrowRight");
export const ArrowUpRight = legacy(LucideArrowUpRight, "ArrowUpRight");
export const Check = legacy(LucideCheck, "Check");
export const ChevronDown = legacy(LucideChevronDown, "ChevronDown");
export const Clone = legacy(LucideCopy, "Clone");
export const CodeBranch = legacy(LucideGitBranch, "CodeBranch");
export const CodeCommit = legacy(LucideGitCommitHorizontal, "CodeCommit");
export const Laptop2 = legacy(LucideLaptop, "Laptop2");
export const Link4 = legacy(LucideLink, "Link4");
export const Menu = legacy(LucideMenu, "Menu");
export const MoonStars = legacy(LucideMoonStar, "MoonStars");
export const Page2 = legacy(LucideFileText, "Page2");
export const Sun = legacy(LucideSun, "Sun");
export const XMark = legacy(LucideX, "XMark");

export function Github({ iconSize = "xl-thin", title, ...props }: LegacyIconProps) {
  const { pixels } = measure(iconSize);
  return <IconGithub width={pixels} height={pixels} aria-label={title ?? "GitHub"} {...props} />;
}
